import { CANONICAL_GHG_CONTRACT_VERSION } from './types';

/** ClimatePlans wastewater methodology document version (Chris-approved). */
export const WASTEWATER_METHODOLOGY_DOCUMENT_VERSION = '1.4';

/** Approved canonical calculation method - only when actually recalculated under this method. */
export const APPROVED_CANONICAL_CALCULATION_METHOD = 'wastewater-1.4';

/** Legacy production calculation methods present in database / precalc (not relabeled). */
export const LEGACY_WASTEWATER_CALCULATION_METHODS = [
  'wastewater-1.0',
  '1.2-county',
  'wastewater-1.5',
  'wastewater-1.3',
] as const;

export type WastewaterMethodClassification =
  | 'canonical'
  | 'legacy'
  | 'noncanonical'
  | 'unavailable';

export interface WastewaterVersionProvenance {
  /** Methodology document version (ClimatePlans_GHG_Methodology_Wastewater.md). */
  methodologyDocumentVersion: string;
  /** Actual calculation-method version used to produce the value. */
  calculationMethodVersion: string | null;
  /** Emission-factor set version (e.g. EF-WW-1.0). */
  emissionFactorVersion: string | null;
  /** Dataset or precalc generation version when known. */
  datasetGenerationVersion: string | null;
  /** Canonical GHG contract version governing inventory assembly. */
  contractVersion: string;
  methodClassification: WastewaterMethodClassification;
  /** True only when calculationMethodVersion === approved canonical method. */
  isCanonicalCalculation: boolean;
}

export function classifyWastewaterCalculationMethod(
  rawMethod: string | null | undefined
): WastewaterMethodClassification {
  if (!rawMethod) return 'unavailable';
  if (rawMethod === APPROVED_CANONICAL_CALCULATION_METHOD) return 'canonical';
  if ((LEGACY_WASTEWATER_CALCULATION_METHODS as readonly string[]).includes(rawMethod)) {
    return 'legacy';
  }
  return 'noncanonical';
}

export function buildWastewaterProvenance(
  calculationMethodVersion: string | null,
  emissionFactorVersion: string | null,
  datasetGenerationVersion?: string | null
): WastewaterVersionProvenance {
  const methodClassification = classifyWastewaterCalculationMethod(calculationMethodVersion);
  return {
    methodologyDocumentVersion: WASTEWATER_METHODOLOGY_DOCUMENT_VERSION,
    calculationMethodVersion,
    emissionFactorVersion,
    datasetGenerationVersion: datasetGenerationVersion ?? null,
    contractVersion: CANONICAL_GHG_CONTRACT_VERSION,
    methodClassification,
    isCanonicalCalculation: methodClassification === 'canonical',
  };
}

/** Normalize DB methodology_version field to calculation-method id without relabeling. */
export function normalizeCalculationMethodFromDb(
  raw: string | null | undefined
): string | null {
  if (raw == null || String(raw).trim() === '') return null;
  return String(raw).trim();
}
